import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function PureTrackerVapingPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="PureTracker: Quit Vaping"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “PureTracker: Quit Vaping” (the “App”) collects,
        uses, stores, and discloses information about users. The App helps you
        track days free from drinking or vaping, view savings and streaks, keep
        a journal, receive motivation, and optionally chat with an AI Coach. Two
        public variants share the same codebase—Quit Drinking and Quit
        Vaping—but differ in content and visuals. Core experiences work offline
        with an on-device cache; synchronized features use Firebase. This Policy
        covers onboarding and optional sign-in, streak tracking, savings and
        stats, motivational content, journaling and mood, achievements,
        subscriptions/entitlements, notifications, analytics/crash diagnostics,
        optional AI Coach, and support.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The controller determining purposes and means of processing is
        Aleksandra Rakitina. For questions or to exercise privacy rights
        (access, deletion, correction, restriction, objection, portability),
        contact alexwadek@gmail.com. We verify identity before fulfilling
        requests, typically by confirming control of the account email or
        requesting minimal additional information. We retain privacy
        correspondence for compliance and security where permitted by law.
      </p>

      <div
        className={s.title}
      >{`3) Key Principles (Supportive, Non-Medical, Minimization, User Control)`}</div>
      <p>
        Supportive, non-medical: the App provides encouragement and
        self-reflection tools; it is not a medical or cessation program.
        Minimization: we collect only what is necessary to deliver requested
        features (auth/sync, subscriptions, analytics for reliability,
        notifications). User control: you choose whether to sign in, what to
        record in your journal, whether to enable AI Coach, who sees shared
        items, and when to delete data. We do not sell Personal Data and avoid
        behavioral advertising SDKs.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information relating to an identified or
        identifiable person. “Progress Data” includes streak counters, savings
        estimates, selected habit type (drinking or vaping), and achievement
        status. “Journal Data” covers notes, mood (e.g., 1–5 emoji), and
        timestamps. “Motivation Data” includes liked/saved quotes/cards.
        “Content Config” refers to localized strings, quotes, and visuals in
        assets or served via Remote Config/Firestore. “Processing” includes
        collection, storage, transformation, transmission, sharing, and
        deletion. “Service Providers/Processors” are third parties acting on our
        instructions (Firebase, RevenueCat, messaging, AI provider via proxy).
      </p>

      <div className={s.title}>5) What We Collect</div>
      <p>
        Depending on your choices and permissions, we process: (a) Account data
        via Firebase Auth if you sign in (email, display name, provider IDs) or
        a local guest identifier if you remain anonymous. (b) Progress Data
        (habit type, start date or reset date, current streak, savings
        assumptions you enter—such as average daily spend—derived savings
        estimates, achievements). (c) Journal Data (free-text notes you enter,
        mood emoji, timestamps). (d) Motivation Data (quotes/cards viewed,
        liked/saved items). (e) Content Config flags from Remote Config and,
        when enabled, content payloads from Firestore. (f)
        Subscription/entitlement metadata from RevenueCat (product IDs, trial
        eligibility, renewal outcome, expiration/cancellation) for premium
        features such as unlimited AI Coach, all motivations, advanced progress
        analytics, and ad-free experience. (g) Analytics and crash diagnostics
        (event counters, anonymized error codes, device/OS/app versions). (h)
        Notification tokens and preferences for push via FCM. We do not receive
        payment card numbers.
      </p>

      <div className={s.title}>6) Sources of Data</div>
      <p>
        Most information originates from you when you set your habit type, track
        streaks, enter savings assumptions, like motivation, and write journal
        entries. Technical data arises automatically from the App and providers
        to maintain reliability. Content (quotes, facts, images) is shipped in
        assets or fetched from Firebase; AI Coach output is generated on request
        through a secured proxy.
      </p>

      <div className={s.title}>7) Offline-First Storage and Sync</div>
      <p>
        The App maintains an offline cache using Hive/SharedPrefs so streaks,
        history, and journal work without a connection. When network
        connectivity is available and you are signed in (if you choose), select
        data syncs to Firestore to support restoration across devices. Guest
        usage stores data only on the device; deleting the App or resetting the
        device may permanently remove guest data.
      </p>

      <div className={s.title}>8) Streaks, Savings, and Stats</div>
      <p>
        Streak counters and savings are computed on-device using your inputs
        (e.g., average daily spend). You can edit assumptions in Settings;
        changes apply prospectively and do not alter previously saved entries
        unless you choose to recalculate. We store streak checkpoints and
        achievements locally and, if signed in, can sync them to Firestore.
        These records are not shared publicly unless you explicitly share a
        result card.
      </p>

      <div className={s.title}>9) Journal and Mood Tracker</div>
      <p>
        Journal entries and mood ratings are created by you and stored locally
        by default (optionally synced when signed in). You can edit or delete
        entries anytime. Avoid including sensitive personal, medical, or
        financial information you do not want processed. If you share a journal
        excerpt externally, the destination’s privacy rules apply and are
        outside our control.
      </p>

      <div className={s.title}>10) Motivation Feed and Saved Items</div>
      <p>
        Motivational cards (quotes, facts, imagery) are delivered from local
        assets or Firebase. You can like/save items for quick access. Likes/saves
        are stored locally and, if signed in, may sync to your account. Some
        sets are premium-only; entitlements determine access.
      </p>

      <div className={s.title}>11) Optional AI Coach (Secure Proxy)</div>
      <p>
        If you use AI Coach, the App sends your prompt and minimal context to a
        serverless proxy (Firebase Functions) that calls an AI model provider
        over HTTPS using our backend key. We do not embed advertising identifiers
        in AI requests. The proxy may log minimal technical metadata (timestamps,
        request size, latency, error codes) for abuse prevention and reliability.
        Conversation snippets are not used for marketing. You can keep AI off by
        not opening the chat; premium plans enable higher limits but do not
        require sharing more data than needed to answer your prompts.
      </p>

      <div className={s.title}>
        12) Subscriptions, Trials, Pricing, and Entitlements
      </div>
      <p>
        Subscriptions use platform billing via RevenueCat and may include weekly
        $4.99 (3-day trial), monthly $14.99, yearly $39.99. Entitlements can
        differ per variant (e.g., puretracker_drink_premium,
        puretracker_vape_premium) but provide similar premium features. We
        receive product identifiers, trial eligibility, renewal outcomes,
        expiration dates, and cancellation status to grant or revoke access.
        Manage or cancel in device account settings; refunds and grace periods
        follow platform rules. We do not receive card details.
      </p>

      <div className={s.title}>
        13) Free Tier Limits and Paywall Gating
      </div>
      <p>
        Results, full motivation access, daily AI limits, and advanced analytics
        may be gated by paywall placements (for example, before showing a full
        result). Free users typically have 1 AI chat per day and a limited
        motivation catalog; premium unlocks unlimited AI Coach and full content.
        Fair-use thresholds may apply to prevent abuse.
      </p>

      <div className={s.title}>14) Notifications and Local Reminders</div>
      <p>
        With your permission, the App may send reminders such as streak
        milestones (“You reached 7 days—keep going!”), daily encouragement, and
        weekly summaries. Delivery requires a device token used solely for
        routing messages. You can disable notifications in system settings and,
        where provided, in-app toggles. We avoid placing sensitive journal
        contents in notification text.
      </p>

      <div className={s.title}>
        15) Analytics, Events, and Crash Diagnostics
      </div>
      <p>
        We use Firebase Analytics and Crashlytics to understand reliability and
        feature adoption—not to build third-party ad profiles. Typical events:
        app_open, start_trial, purchase, ai_message, journal_add, plus
        view/click counters and anonymized error codes. Where feasible, analytics
        are aggregated/pseudonymized and exclude journal text and AI message
        bodies. Platform privacy controls can reduce analytics but may limit
        troubleshooting.
      </p>

      <div className={s.title}>16) Remote Config and Experiments</div>
      <p>
        We may use Firebase Remote Config to adjust non-personalized settings
        (e.g., copy on paywalls, trial offers within platform rules, content pack
        toggles). Experiments use minimal identifiers and aggregated metrics to
        evaluate performance; we do not store your journal or AI messages for
        marketing.
      </p>

      <div className={s.title}>17) What We Do Not Collect or Sell</div>
      <p>
        We do not sell Personal Data. We do not collect payment card numbers. We
        do not require precise geolocation or contacts for core features. We do
        not share your Journal Data or AI messages with ad networks. We do not
        claim clinical or therapeutic effectiveness and do not collect medical
        records.
      </p>

      <div className={s.title}>
        18) Legal Bases (EEA/UK) and Global Principles
      </div>
      <p>
        Legal bases include performance of a contract (streak tracking, sync,
        subscriptions), legitimate interests (security, fraud prevention, product
        improvement) balanced against your rights through minimization and
        controls, consent (notifications, certain analytics categories, AI Coach
        where required), and legal obligations (records, lawful requests). We
        will seek additional consent if purposes materially change.
      </p>

      <div className={s.title}>19) Security Measures</div>
      <p>
        We apply layered safeguards: HTTPS for all provider communications;
        encryption at rest for Firestore/Storage; rules-based access control;
        least-privilege access to operational tools; environment separation;
        credential rotation; monitoring for abuse. No system is perfectly secure,
        but our offline-first design reduces exposure by keeping most user
        content on-device unless you opt into sync or sharing.
      </p>

      <div className={s.title}>20) Data Retention and Deletion</div>
      <p>
        Local data (Progress, Journal, Motivation saves) persists until you
        delete it or uninstall the App. If you use a signed-in account with
        sync, Firestore records remain until you delete them or request account
        deletion. Provider backups/replication may introduce short delays before
        full purge. Subscription/entitlement metadata may be retained longer for
        accounting and fraud prevention in pseudonymized or aggregated form.
        Crash/analytics logs are preserved for operational windows then
        de-identified or deleted.
      </p>

      <div className={s.title}>
        21) Children’s Privacy and Sensitive Uses
      </div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by law. Content focuses on habit change for adults and avoids
        glamorizing alcohol or nicotine. If you believe a minor used the App
        without appropriate authorization, contact alexwadek@gmail.com and we
        will remove information and restrict access as appropriate.
      </p>

      <div className={s.title}>22) Sharing and Exports</div>
      <p>
        If you share a streak card or achievement externally, the App creates an
        image or text summary and hands it to the system share sheet. Exports and
        shared content are controlled by the destination services (messages,
        social apps) and their policies. Deleting an item in the App does not
        remove copies you already shared elsewhere.
      </p>

      <div className={s.title}>
        23) Do Not Track, Identifiers, and Cookies
      </div>
      <p>
        As a native app, we do not use browser cookies. SDKs may store
        identifiers for authentication, sync, analytics, crash diagnostics,
        notifications, and subscriptions. These function similarly to cookies by
        enabling sessions and measurement. The App does not rely on advertising
        IDs for targeted ads. Browser “Do Not Track” signals generally do not
        apply to native apps.
      </p>

      <div className={s.title}>
        24) Third-Party Providers and Disclosures
      </div>
      <p>
        We rely on vetted providers acting on our instructions: Firebase (Auth
        optional, Firestore, Storage, Remote Config, Analytics, Messaging,
        Crashlytics, Functions), RevenueCat (subscriptions), and an AI model
        provider accessed via a secure proxy. Providers must implement
        appropriate security and are prohibited from using Personal Data for
        independent marketing. We may disclose information when legally required
        by competent authorities after validating scope and jurisdiction, or in a
        business transition where a successor honors this Policy or provides
        notice of changes.
      </p>

      <div className={s.title}>
        25) Accuracy, Limitations, and Health Disclaimer
      </div>
      <p>
        The App is for encouragement and self-reflection only. Streaks and
        savings depend on your inputs and assumptions and may not reflect
        clinical outcomes. AI Coach messages are generated and may be incomplete
        or inaccurate. The App does not diagnose, treat, or provide medical or
        cessation advice. If you need medical guidance, consult qualified
        professionals.
      </p>

      <div className={s.title}>
        26) Fair Use, Abuse Prevention, and Limits
      </div>
      <p>
        To maintain reliability and cost control, we may apply fair-use
        thresholds (e.g., excessive automated AI prompts), rate-limits, and
        entitlement checks. Attempts to bypass limits or misuse subscriptions may
        trigger protective measures such as temporary lockouts. Material changes
        to limits follow platform rules and applicable law.
      </p>

      <div className={s.title}>27) International Data Transfers</div>
      <p>
        Depending on your and our providers’ locations, data may be processed in
        jurisdictions with different data-protection rules. Where required, we
        rely on appropriate safeguards for cross-border transfers (contractual
        protections and platform mechanisms). Regardless of location, providers
        must implement security measures consistent with this Policy and
        applicable law.
      </p>

      <div className={s.title}>28) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur.
        Material changes affecting your rights or our processing will be
        highlighted in-app; where required, we will request consent. Continued
        use after changes take effect signifies acceptance.
      </p>

      <div className={s.title}>
        29) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com from your sign-in
        address (if applicable) and specify the request type (access, deletion,
        correction, restriction, objection, portability). Include helpful context
        such as device model and app version. We verify identity to prevent
        unauthorized access or deletion and may request limited additional
        information or a confirmation code. Where feasible, responses are
        provided in a portable, machine-readable format. We may decline or limit
        requests when permitted by law (for example, if fulfilling a request
        would compromise security, interfere with legal obligations, or infringe
        on others’ privacy).
      </p>
    </PolicyLayout>
  );
}
